import {
  Directive,
  inject,
  Injector,
  input,
  runInInjectionContext,
  type SimpleChanges,
} from '@angular/core';
import type { AbstractControl, FormControl } from '@angular/forms';
import { FormControlDirective, FormControlName } from '@angular/forms';

import { ProtectedControlsContext } from './protected-controls.context';
import { ProtectedControlsService } from './protected-controls.service';

type AcceptedControl = AbstractControl | '' | undefined | null;

export interface ProtectedControlMeta {
  context: ProtectedControlsContext;
  message?: string;
  name?: string;
  newValue?: any;
  oldValue?: any;
}

export interface ProtectedControlOptions {
  /**
   @desc Array of property getters on AbstractControl: https://angular.io/api/forms/AbstractControl
    Used for producing "locked" state (= control[bailOnProp[0]] && control[bailOnProp[1]] && control[bailOnProp[2]] ...)

    Valid props are: ['dirty', 'valid', 'invalid', 'pending', 'enabled', 'pristine', 'touched', 'untouched']
   */

  bailOn: Array<
    'dirty' | 'valid' | 'invalid' | 'pending' | 'enabled' | 'pristine' | 'touched' | 'untouched'
  >;
  bailOnlyIfChanged: boolean;
}

@Directive({
  selector: '[rtProtectedControl]',
  exportAs: 'rtProtectedControl',
  standalone: true,
})
export class ProtectedControlDirective {
  private readonly injector = inject(Injector);

  public readonly name = input<string>('unknown', { alias: 'rtProtectedControlName' });
  public readonly options = input<ProtectedControlOptions>(
    {
      bailOn: ['dirty'],
      bailOnlyIfChanged: false,
    },
    { alias: 'rtProtectedControlOptions' },
  );
  public readonly message = input<string>(undefined!, { alias: 'rtProtectedControlMessage' });

  public readonly control = input<AcceptedControl>(undefined, { alias: 'rtProtectedControl' });

  public rtProtectedControlEnabled = input<boolean>(true);

  private handledControl: AbstractControl;

  public get meta(): ProtectedControlMeta {
    return {
      context: this.context,
      message: this.message(),
      name: this.name(),
      newValue: this.handledControl?.value,
      oldValue: this._initialValue,
    };
  }

  private _initialValue: null;

  constructor(
    private srvc: ProtectedControlsService,
    private context: ProtectedControlsContext,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.control) {
      this.handledControl = this.resolveHandledFormControl();
    }
  }

  public ngOnInit(): void {
    if (!this.rtProtectedControlEnabled()) {
      return;
    }

    this.srvc.register(this);
  }

  public ngAfterViewInit(): void {
    this.handledControl = this.resolveHandledFormControl();
    this._initialValue = this.handledControl.value;
  }

  public ngOnDestroy(): void {
    if (!this.rtProtectedControlEnabled()) {
      return;
    }

    this.srvc.unregister(this);
  }

  public get locked(): boolean {
    if (!this.handledControl) {
      return false;
    }
    return this.isControlLocked();
  }

  private isControlLocked(): boolean {
    const options = this.options();
    if (options.bailOnlyIfChanged) {
      if (this._initialValue === this.handledControl!.value) {
        return false;
      }
    }

    return options.bailOn.some(opt => this.handledControl![opt]);
  }

  private resolveHandledFormControl(): FormControl {
    const control = this.control();
    if (!control) {
      return runInInjectionContext(this.injector, () => {
        const formControlNameDirective = inject(FormControlName, { self: true, optional: true });
        if (formControlNameDirective) return formControlNameDirective.control;

        const formControlDirective = inject(FormControlDirective, { self: true, optional: true });
        if (formControlDirective) return formControlDirective.control;

        throw new Error(`rtProtectedControl couldn't hook to FormControl`);
      });
    }

    return <FormControl>control;
  }
}
